import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import "./OnlineTestSeriesLandingPage.css";
import "react-loading-skeleton/dist/skeleton.css";
import { Link, useParams } from "react-router-dom";
import { formatSlugUpperCase, slugify, formatSlug } from "../../utils/slugify";
import { getRequest } from "../../services/api";
import { normalizeSeriesType } from "../../utils/stringFormat";

interface BlogPost {
  _id: string;
  slug: string;
  header_title: string;
  url_title: string;
  category_name: string;
  test_category_name: string;
  sub_category_name: string;
  image: string;
  posted_by: string;
  updated_at: string;
  views: number;
  likes: number;
  test_series_type: string;
  description: string;
  flagship: string;
  discounted_price: string;
  original_price: string;
  rating: string;
  students_count: string;
  header_sub_title: string;
  test_count: string;
  features: string[];
}

interface WhyItem {
  title: string;
  desc: string;
}

/* ─── SVG Icons ──────────────────────────────────────────────────────────── */
const CheckIcon: React.FC<{ size?: number }> = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const BookIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);
const StarIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24"
    fill="#fbbf24" stroke="#fbbf24" strokeWidth="1"
    strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);
const UsersIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const ClockIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);
const ChevronRight: React.FC = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const FileTextIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);
const EyeIcon: React.FC<{ size?: number }> = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

/* ─── Hero Stat pill ─────────────────────────────────────────────────────── */
interface HeroStatProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  isFirst?: boolean;
}
const HeroStat: React.FC<HeroStatProps> = ({ label, value, icon, isFirst = false }) => (
  <div className="ldp-hero-stat" style={isFirst ? { borderLeft: "none" } : {}}>
    <div className="ldp-hero-stat-label">
      {icon}
      {label}
    </div>
    <div className="ldp-hero-stat-value">{value}</div>
  </div>
);

/* ─── Static sidebar MCQ data ────────────────────────────────────────────── */
const MCQ_ITEMS = [
  { title: "JEE Main Physics MCQs - Wave Optics",        qs: "25 Qs", views: "12.5K", tagCls: "ldp-tag-physics",     tag: "Physics",   time: "2 days ago" },
  { title: "NEET Biology - Cell Structure and Function", qs: "30 Qs", views: "18.2K", tagCls: "ldp-tag-biology",     tag: "Biology",   time: "3 days ago" },
  { title: "SSC CGL Reasoning Ability Questions",        qs: "40 Qs", views: "9.8K",  tagCls: "ldp-tag-reasoning",   tag: "Reasoning", time: "5 days ago" },
  { title: "IBPS PO Quantitative Aptitude Practice",     qs: "35 Qs", views: "15.3K", tagCls: "ldp-tag-mathematics", tag: "Maths",     time: "1 week ago" },
  { title: "Class 12 Chemistry - Organic Reactions",     qs: "20 Qs", views: "11.7K", tagCls: "ldp-tag-chemistry",   tag: "Chemistry", time: "1 week ago" },
];

/* ─── Why items fallback ─────────────────────────────────────────────────── */
const DEFAULT_WHY: WhyItem[] = [
  { title: "Latest Exam Pattern",   desc: "All tests follow the latest exam pattern and syllabus" },
  { title: "Detailed Solutions",    desc: "Get comprehensive explanations for every question" },
  { title: "Performance Analysis",  desc: "Track your progress with detailed analytics" },
  { title: "All-India Ranking",     desc: "Compare your performance with other aspirants" },
];

/* ════════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════════════════════ */
const OnlineTestSeriesLandingPage: React.FC = () => {
  const [postsBlogs,    setPostsBlogs]    = useState<BlogPost[]>([]);
  const [loading,       setLoading]       = useState(true);
  const [seriesDetails, setSeriesDetails] = useState<any>(null);

  const { category, testwise } = useParams();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getRequest("/blog-sub-category", {
          blog_post_category_name: "test_series",
          test_series_type: normalizeSeriesType(testwise),
          test_category_name: category,
        });
        setPostsBlogs(response.data.blogs);

        const seriesRes = await getRequest("/test-series", {
          category: "Test Series",
          test_series_type: normalizeSeriesType(testwise),
          sub_category_name: category,
          limit: 6,
        });
        if (seriesRes.data.testSeries?.length > 0) {
          setSeriesDetails(seriesRes.data.testSeries[0]);
        }
      } catch (error) {
        console.error("Error fetching landing page data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [category, testwise]);

  const whyList: WhyItem[] = seriesDetails?.whyItems?.length
    ? seriesDetails.whyItems
    : DEFAULT_WHY;

  return (
    <>
      <Helmet>
        <title>Acadebility - {formatSlugUpperCase(category || "")} Test Series</title>
        <meta name="description" content={`${formatSlugUpperCase(category || "")} Online Test Series`} />
      </Helmet>

      {/* ── Full-width page wrapper ── */}
      <div className="ldp-page">

        {/* ── Inner content area (max-width + padding) ── */}
        <div className="ldp-inner">

          {/* ── Back button ── */}
          {/* <Link to="/online-test-series/" className="ldp-back-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back
          </Link> */}
            {/* Back Button */}
                    <div style={{ display: "flex", justifyContent: "flex-start" }}>
                      <Link to={`/online-test-series`}>
                        <button
                          type="button"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 7,
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            fontFamily: "'Nunito', sans-serif",
                            fontWeight: 700,
                            fontSize: "0.9rem",
                            color: "#1a2e2b",
                            marginBottom: 20,
                            padding: 0,
                          }}
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#1a2e2b"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="19" y1="12" x2="5" y2="12" />
                            <polyline points="12 19 5 12 12 5" />
                          </svg>
                          Back
                        </button>
                      </Link>
                    </div>
          {/* ══════════════════════════════════════════════════════════
              HERO BANNER
          ══════════════════════════════════════════════════════════ */}
          <div className="ldp-hero">

            {/* Left: icon + title */}
            <div className="ldp-hero-left">
              <div className="ldp-hero-icon">📚</div>
              <div className="ldp-hero-text">
                <h1 className="ldp-hero-title">{formatSlugUpperCase(category || "")} Test Series</h1>
                <p className="ldp-hero-sub">Engineering Exam Preparation</p>
              </div>
            </div>

            {/* Right: stat pills */}
            <div className="ldp-hero-stats">
              <HeroStat isFirst label="Tests" value="45+"
                icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="rgba(255,255,255,0.85)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                </svg>}
              />
              <HeroStat label="Students" value="2.5L+"
                icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="rgba(255,255,255,0.85)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>}
              />
              <HeroStat label="Duration" value="3 hrs"
                icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="rgba(255,255,255,0.85)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                </svg>}
              />
              <HeroStat label="Rating" value="4.8/5"
                icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="#fbbf24"
                  stroke="#fbbf24" strokeWidth="1">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>}
              />
            </div>
          </div>

          {/* ══════════════════════════════════════════════════════════
              BODY: main content (left) + sidebar (right)
          ══════════════════════════════════════════════════════════ */}
          <div className="ldp-body">

            {/* ── LEFT COLUMN ── */}
            <div className="ldp-main">

              {/* ── PACKAGES ── */}
              <div className="ldp-section-head">
                <div>
                  <h2 className="ldp-section-title">Available Test Series Packages</h2>
                  <p className="ldp-section-sub">
                    Choose the perfect package for your {formatSlugUpperCase(category || "")} preparation
                  </p>
                </div>
              </div>

              {loading ? (
                <div className="ldp-packages-grid">
                  {[1, 2, 3, 4].map(i => (
                    <div className="ldp-pkg-card ldp-pkg-skeleton" key={i} />
                  ))}
                </div>
              ) : postsBlogs?.length > 0 ? (
                <div className="ldp-packages-grid">
                  {postsBlogs.map((pkg: BlogPost, index: number) => (
                    <div
                      className={`ldp-pkg-card${pkg.flagship ? " ldp-pkg-flagship" : ""}`}
                      key={index}
                    >
                      {/* Flagship ribbon */}
                      {pkg.flagship && (
                        <div className="ldp-flagship-ribbon">
                          <StarIcon size={12} />
                          Flagship Product
                        </div>
                      )}

                      {/* Card top: name + desc */}
                      <div className="ldp-pkg-top">
                        <div className="ldp-pkg-name">{formatSlug(pkg.header_title)}</div>
                        <div className="ldp-pkg-desc">{pkg.header_sub_title}</div>
                      </div>

                      {/* Price row */}
                      <div className="ldp-pkg-price-row">
                        <span className="ldp-pkg-price">₹{pkg.discounted_price}</span>
                        <span className="ldp-pkg-price-old">₹{pkg.original_price}</span>
                        {pkg.original_price && pkg.discounted_price && (
                          <span className="ldp-pkg-discount-tag">
                            {Math.round((1 - Number(pkg.discounted_price) / Number(pkg.original_price)) * 100)}% OFF
                          </span>
                        )}
                      </div>

                      {/* Rating + students */}
                      <div className="ldp-pkg-rating-row">
                        <span className="ldp-pkg-stars">
                          {[1,2,3,4,5].map(s => (
                            <StarIcon key={s} size={13} />
                          ))}
                        </span>
                        <span className="ldp-pkg-rating-val">{pkg.rating}</span>
                        <span className="ldp-pkg-dot">·</span>
                        <UsersIcon size={13} />
                        <span className="ldp-pkg-students">{pkg.students_count} students</span>
                      </div>

                      {/* Meta pills */}
                      <div className="ldp-pkg-meta-pills">
                        <span className="ldp-meta-pill">
                          <BookIcon size={13} />
                          {pkg.test_count} Tests
                        </span>
                        <span className="ldp-meta-pill">
                          <ClockIcon size={13} />
                          3 hrs / test
                        </span>
                        <span className="ldp-meta-pill ldp-meta-pill-free">
                          Free Trial
                        </span>
                      </div>

                      {/* Divider */}
                      <div className="ldp-pkg-divider" />

                      {/* Features */}
                      <ul className="ldp-pkg-features">
                        {pkg.features?.map((f: string) => (
                          <li key={f}>
                            <span className="ldp-feature-check"><CheckIcon size={13} /></span>
                            {f}
                          </li>
                        ))}
                      </ul>

                      {/* CTA */}
                      <Link
                        to={`/online-test-series/${testwise}/${slugify(pkg.test_category_name)}/${pkg.slug}`}
                        className="ldp-pkg-cta"
                      >
                        View Test Series
                        <ChevronRight />
                      </Link>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="ldp-empty">No Test Series found for this category.</div>
              )}

              {/* ── ABOUT ── */}
              <div className="ldp-about">
                <h2 className="ldp-about-title">About {formatSlugUpperCase(category || "")} Test Series</h2>
                <p>{seriesDetails?.about}</p>
                {seriesDetails?.about_sub && <p>{seriesDetails.about_sub}</p>}
              </div>

              {/* ── WHY CHOOSE ── */}
              <div className="ldp-why">
                <h2 className="ldp-why-title">Why Choose Our Test Series?</h2>
                <div className="ldp-why-grid">
                  {whyList.map((item: WhyItem) => (
                    <div className="ldp-why-item" key={item.title}>
                      <div className="ldp-why-check">
                        <CheckIcon size={15} />
                      </div>
                      <div>
                        <div className="ldp-why-item-title">{item.title}</div>
                        <div className="ldp-why-item-desc">{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>{/* end ldp-main */}

            {/* ── RIGHT SIDEBAR ── */}
            <aside className="ldp-sidebar">
              <div className="ldp-sidebar-title">Recent MCQ Posts</div>
              <div className="ldp-mcq-list">
                {MCQ_ITEMS.map((post) => (
                  <div className="ldp-mcq-card" key={post.title}>
                    <div className="ldp-mcq-icon">
                      <FileTextIcon size={16} />
                    </div>
                    <div className="ldp-mcq-info">
                      <div className="ldp-mcq-title">{post.title}</div>
                      <div className="ldp-mcq-meta">
                        <span><BookIcon size={11} /> {post.qs}</span>
                        <span><EyeIcon size={11} /> {post.views}</span>
                      </div>
                      <div className="ldp-mcq-footer">
                        <span className={`ldp-tag ${post.tagCls}`}>{post.tag}</span>
                        <span className="ldp-mcq-time">
                          <ClockIcon size={11} />
                          {post.time}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </aside>

          </div>{/* end ldp-body */}
        </div>{/* end ldp-inner */}
      </div>{/* end ldp-page */}
    </>
  );
};

export default OnlineTestSeriesLandingPage;
